import { FX_FIRE, fxAt, fxCount, type Frame, type TowerKind } from "@empire/core-sim";
import { FLIGHT_TICKS, type UnitFx } from "./fx.js";

/** Select authored stages from replay time, never from a second animation clock. */
export function unitAction(
  state: UnitFx | undefined,
  ghost: boolean,
  moving: boolean,
  reduced: boolean
) {
  if (ghost && !state?.reached) {
    if (reduced) return "fallen";
    const age = state?.fade ?? 0;
    return age < 0.12 ? "hit" : age < 0.3 ? "collapse_0" : age < 0.52 ? "collapse_1" : "fallen";
  }
  if (ghost) return null;
  if (!reduced && state && state.impact > 0.12) {
    return state.impact > 0.68 ? "hit" : "recover";
  }
  return moving ? null : "ready";
}

export function idleBreath(kind: string, id: number, position: number, reduced: boolean) {
  return reduced || kind === "ram" ? 1 : 1 + Math.sin(position * 0.19 + id * 1.7) * 0.004;
}

/** Event age relative to the existing visible projectile launch, in ticks. */
export function towerActions(
  frames: readonly Frame[],
  position: number
): ReadonlyMap<number, number> {
  const result = new Map<number, number>();
  const now = Math.max(0, Math.min(position, frames.length - 1));
  for (
    let f = Math.max(0, Math.floor(now - 7));
    f <= Math.min(frames.length - 1, Math.ceil(now + FLIGHT_TICKS + 1.5));
    f++
  ) {
    const record = frames[f]!;
    for (let i = 0; i < fxCount(record.fx); i++) {
      const event = fxAt(record.fx, i);
      if (event.code !== FX_FIRE || event.a < 0) continue;
      const age = now - (f - FLIGHT_TICKS);
      if (age < -1.5 || age >= 7) continue;
      // A new preparation supersedes the preceding recovery only once it starts.
      result.set(event.a, age);
    }
  }
  return result;
}

/** Anticipate, release, recoil and recover; stone foundations are never transformed. */
export function towerAction(kind: TowerKind, age: number | undefined, reduced: boolean) {
  if (age === undefined || reduced) return { charge: 0, recoil: 0, flash: 0, frame: 0 };
  if (age < 0) return { charge: Math.max(0, 1 + age / 1.5), recoil: 0, flash: 0, frame: 0 };
  const release = kind === "cannon" ? 0.24 : kind === "ballista" ? 0.32 : 0.45;
  const recovery = kind === "cannon" ? 3.5 : kind === "ballista" ? 3.1 : 2.6;
  const recoil =
    age < release ? age / release : Math.pow(Math.max(0, 1 - (age - release) / recovery), 2);
  return {
    charge: 0,
    recoil: recoil * (kind === "cannon" ? 3.8 : kind === "ballista" ? 2.2 : 0.65),
    flash: Math.max(0, 1 - age / 1.1),
    frame: age < 1.2 ? 1 : age < 4 ? 2 : age < 6.5 ? 3 : 0,
  };
}

export function workerAction(seconds: number, delivering = false, reduced = false) {
  const phase = reduced ? 0 : ((seconds % 2.4) + 2.4) % 2.4;
  const frame = phase < 0.75 ? 0 : phase < 1.2 ? 1 : phase < 1.5 ? 2 : 3;
  return `worker_${delivering ? "deliver" : "work"}_${frame}`;
}
